import React, { useEffect, useState, useRef } from "react";
import API from "../utils/api";
import { useLocation } from "react-router-dom";
import { useCountry } from "../context/CountryContext";
import { generateVariedAmount, NG_BASE_AMOUNT, GH_BASE_AMOUNT } from "../utils/paymentAmount";

// ===== CONSTANTS =====
const NOMBA_PAYMENT_LINK = "https://paystack.shop/pay/zap23r8b8e";
const SUPPORT_EMAIL = "probanksupport@gmail.com";
const SUPPORT_WHATSAPP = "27652611261";

// ===== HELPER: always reads fresh from localStorage =====
const getStoredUser = () => {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return {};
    return JSON.parse(raw) || {};
  } catch { return {}; }
};

const PaymentPage = () => {
  const { isGhana, config } = useCountry();

  const [step, setStep] = useState("select");
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState("");
  const [countdown, setCountdown] = useState(5);
  const [form, setForm] = useState({ name: "", email: "" });

  const location = useLocation();
  const userId = new URLSearchParams(location.search).get("userId");

  // ✅ Read the amount Register.js already generated and passed through
  // the URL — this guarantees both screens show the identical figure.
  // Only generate a fresh one here as a fallback, for the edge case of
  // someone landing on /payment directly without an amount param (e.g. a
  // bookmarked or manually-typed URL).
  const amountRef = useRef((() => {
    const fromUrl = new URLSearchParams(location.search).get("amount");
    const parsed = Number(fromUrl);
    if (fromUrl && !isNaN(parsed) && parsed > 0) {
      return parsed;
    }
    return generateVariedAmount(isGhana ? GH_BASE_AMOUNT : NG_BASE_AMOUNT);
  })());

  const theme = isGhana
    ? {
        amount: amountRef.current,
        currencySymbol: "₵",
        bgGradient: "from-[#0a0500] via-black to-[#1a0a00]",
        glow1: "bg-[radial-gradient(circle_at_top_right,_rgba(250,204,21,0.14),_transparent)]",
        glow2: "bg-[radial-gradient(circle_at_bottom_left,_rgba(220,38,38,0.10),_transparent)]",
        primaryGradient: "from-yellow-400 to-red-500",
        accentBorder: "border-yellow-400/40",
        accentBg: "bg-yellow-500/10",
        amountText: "text-yellow-400",
        focusRing: "focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/30",
        completeBtn: "bg-yellow-500 hover:bg-yellow-600 text-black font-semibold",
        flag: "🇬🇭",
        bankRowLabel: "Provider",
        bankRowValue: "Telecel Cash",
        accountRowLabel: "MoMo Number",
        accountNumber: "0507557845",
        accountName: "LUCY ASARE",
        countryCode: "GH",
        currency: "GHS",
      }
    : {
        amount: amountRef.current,
        currencySymbol: "₦",
        bgGradient: "from-gray-950 via-black to-gray-900",
        glow1: "bg-[radial-gradient(circle_at_top_right,_rgba(34,197,94,0.12),_transparent)]",
        glow2: "bg-[radial-gradient(circle_at_bottom_left,_rgba(59,130,246,0.08),_transparent)]",
        primaryGradient: "from-green-400 to-emerald-500",
        accentBorder: "border-green-400/40",
        accentBg: "bg-green-500/10",
        amountText: "text-green-400",
        focusRing: "focus:border-green-400 focus:ring-2 focus:ring-green-400/30",
        completeBtn: "bg-green-500 hover:bg-green-600 text-white font-semibold",
        flag: "🇳🇬",
        bankRowLabel: "Bank",
        bankRowValue: "Moniepoint MFB",
        accountRowLabel: "Account",
        accountNumber: "",
        accountName: "",
        countryCode: "NG",
        currency: "NGN",
      };

  useEffect(() => {
    const stored = getStoredUser();
    if (stored.name || stored.email) {
      setForm({
        name: stored.name || "",
        email: stored.email || "",
      });
    }
  }, []);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3000);
  };

  useEffect(() => {
    if (step !== "instructions") return;
    setCountdown(5);
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) { clearInterval(timer); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [step]);

  const handleSubmit = async () => {
    if (!form.name || !form.email) {
      return showToast("⚠️ Please fill all fields");
    }
    if (isGhana) { setStep("details"); return; }
    try {
      setLoading(true);
      await API.post("/payments/manual-payment", {
        userId,
        name: form.name,
        email: form.email,
        amount: theme.amount,
        country: theme.countryCode,
        currency: theme.currency,
      });
      setStep("instructions");
    } catch (err) {
      showToast("❌ " + (err.response?.data?.message || "Failed to start payment"));
    } finally {
      setLoading(false);
    }
  };

  const copyAmount = () => {
    navigator.clipboard.writeText(String(theme.amount));
    showToast(`✅ ${theme.currencySymbol}${theme.amount.toLocaleString()} copied!`);
  };

  const copyAccount = () => {
    navigator.clipboard.writeText(theme.accountNumber);
    showToast("✅ Account number copied");
  };

  const buildMessages = () => {
    const stored = getStoredUser();
    const name = form.name || stored.name || stored.fullName || "Not provided";
    const email = form.email || stored.email || "Not provided";
    const uid = userId || stored._id || stored.id || "Not provided";

    const proofBody = `Hello ProBank Support,\n\nI have completed my registration payment.\n\nName: ${name}\nEmail: ${email}\nUser ID: ${uid}\nAmount: ${theme.currencySymbol}${theme.amount.toLocaleString()}\nCountry: ${theme.flag} ${config.name}\n\nKindly verify and activate my account.\n\nThank you.`;

    const helpBody = `Hello ProBank Support,\n\nI am having trouble with my registration payment.\n\nName: ${name}\nEmail: ${email}\nUser ID: ${uid}\nAmount: ${theme.currencySymbol}${theme.amount.toLocaleString()}\nCountry: ${theme.flag} ${config.name}\n\nPlease assist me.\n\nThank you.`;

    const waProof = encodeURIComponent(`Hello ProBank Support, I have completed my registration payment.\n\nName: ${name}\nEmail: ${email}\nUser ID: ${uid}\nAmount: ${theme.currencySymbol}${theme.amount.toLocaleString()}\n\nPlease activate my account.`);

    const waHelp = encodeURIComponent(`Hello ProBank Support, I am having trouble with my registration payment.\n\nName: ${name}\nEmail: ${email}\nAmount: ${theme.currencySymbol}${theme.amount.toLocaleString()}\n\nPlease help me.`);

    return {
      emailProof: `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent("Registration Payment Proof - " + name)}&body=${encodeURIComponent(proofBody)}`,
      emailHelp: `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent("Registration Payment Problem - " + name)}&body=${encodeURIComponent(helpBody)}`,
      waProof: `https://wa.me/${SUPPORT_WHATSAPP}?text=${waProof}`,
      waHelp: `https://wa.me/${SUPPORT_WHATSAPP}?text=${waHelp}`,
    };
  };

  const OPayWarning = () => (
    <div className="p-4 rounded-2xl bg-red-500/15 border-2 border-red-500/60">
      <div className="flex items-start gap-3">
        <span className="text-2xl flex-shrink-0">🚫</span>
        <div>
          <p className="font-bold text-red-300 text-sm mb-1">DO NOT PAY FROM OPAY</p>
          <p className="text-red-200 text-xs leading-relaxed">
            We do <span className="font-bold underline">NOT</span> accept OPay payments.
            Use GTBank, Access, Zenith, UBA, First Bank, Kuda, Moniepoint, Palmpay or any other bank.
          </p>
        </div>
      </div>
    </div>
  );

  const SupportButtons = ({ proof = false }) => {
    const msgs = buildMessages();
    return (
      <div className="space-y-2">
        <p className="text-xs text-gray-400 text-center">
          {proof ? "Send your payment proof:" : "Having payment trouble? Contact us:"}
        </p>
        <a
          href={proof ? msgs.waProof : msgs.waHelp}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-green-600 hover:bg-green-700 transition font-semibold text-white text-sm shadow-lg"
        >
          💬 {proof ? "Send Proof on WhatsApp (Fastest)" : "WhatsApp Support (Fastest)"}
        </a>
        <a
          href={proof ? msgs.emailProof : msgs.emailHelp}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 transition font-semibold text-white text-sm shadow-lg"
        >
          📧 {proof ? "Send Proof by Email" : "Email Support"}
        </a>
      </div>
    );
  };

  return (
    <div className={`relative min-h-screen flex items-center justify-center bg-gradient-to-br ${theme.bgGradient} text-white px-4 overflow-hidden py-8`}>
      <div className={`absolute inset-0 ${theme.glow1} pointer-events-none`} />
      <div className={`absolute inset-0 ${theme.glow2} pointer-events-none`} />

      <div className="absolute top-4 right-4 z-30 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zinc-900/60 border border-zinc-700/50 backdrop-blur-md">
        <span className="text-base">{theme.flag}</span>
        <span className="text-xs text-slate-300 font-medium">{config.name}</span>
      </div>

      {toast && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 bg-black/80 backdrop-blur-xl border border-white/10 px-5 py-3 rounded-xl text-sm shadow-2xl">
          {toast}
        </div>
      )}

      <div className="w-full max-w-md bg-white/5 backdrop-blur-2xl border border-white/10 rounded-3xl p-6 shadow-2xl">
        <div className="mb-5 text-center">
          <h2 className="text-2xl font-semibold tracking-wide">Registration Payment</h2>
          <p className="text-gray-400 text-sm mt-1">
            {form.name ? `Welcome, ${form.name}` : "Secure • Fast • Encrypted"}
          </p>
        </div>

        {step === "select" && (
          <div className="space-y-4">
            <OPayWarning />

            <div className={`p-4 rounded-xl ${theme.accentBg} border ${theme.accentBorder} text-center`}>
              <p className="text-xs text-gray-400 mb-1">Registration Fee</p>
              <p className={`text-3xl font-bold ${theme.amountText}`}>
                {theme.currencySymbol}{theme.amount.toLocaleString()}
              </p>
            </div>

            <input
              type="text"
              placeholder="Full Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className={`w-full p-3 rounded-xl bg-black/40 border border-white/10 ${theme.focusRing} outline-none text-white`}
            />
            <input
              type="email"
              placeholder="Email Address"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className={`w-full p-3 rounded-xl bg-black/40 border border-white/10 ${theme.focusRing} outline-none text-white`}
            />

            <button
              onClick={handleSubmit}
              disabled={loading}
              className={`w-full py-4 rounded-xl bg-gradient-to-r ${theme.primaryGradient} font-bold text-black transition shadow-lg ${loading ? "opacity-60 cursor-not-allowed" : "hover:brightness-110"}`}
            >
              {loading ? "Please wait..." : "Continue to Payment"}
            </button>

            <SupportButtons proof={false} />
          </div>
        )}

        {step === "instructions" && (
          <div className="space-y-4">
            <OPayWarning />

            <div className="p-5 rounded-2xl bg-gradient-to-br from-green-500/20 to-emerald-500/10 border-2 border-green-400/40 space-y-3">
              <p className="text-center text-green-300 font-semibold text-sm">Step 1: Copy the amount</p>
              <button
                onClick={copyAmount}
                className="w-full p-4 rounded-xl bg-black/40 border border-green-400/30 hover:bg-black/60 transition flex items-center justify-between"
              >
                <span className="text-2xl font-bold text-green-400">
                  {theme.currencySymbol}{theme.amount.toLocaleString()}
                </span>
                <span className="text-xs bg-green-500 text-black font-bold px-3 py-2 rounded-lg">📋 TAP TO COPY</span>
              </button>
              <p className="text-center text-xs text-green-200/80">Paste this on the payment page</p>
            </div>

            <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-400/30 space-y-2">
              <p className="text-blue-300 font-semibold text-sm">📲 On the payment page:</p>
              <ol className="text-blue-100 text-sm space-y-1.5 pl-1">
                <li><b className="text-blue-400">2.</b> Paste <b className="text-green-300">{theme.amount.toLocaleString()}</b> in the amount field</li>
                <li><b className="text-blue-400">3.</b> Choose Card, Bank Transfer, or USSD</li>
                <li><b className="text-blue-400">4.</b> Complete the payment</li>
                <li><b className="text-blue-400">5.</b> You'll be brought back here automatically</li>
              </ol>
            </div>

            <button
              onClick={() => window.location.href = NOMBA_PAYMENT_LINK}
              disabled={countdown > 0}
              className={`w-full py-4 rounded-xl font-bold text-base shadow-lg transition-all ${
                countdown > 0
                  ? "bg-gray-600 cursor-not-allowed text-gray-300"
                  : `bg-gradient-to-r ${theme.primaryGradient} hover:brightness-110 text-black`
              }`}
            >
              {countdown > 0 ? `Please read first... (${countdown}s)` : "✅ Take Me to Payment"}
            </button>

            <button onClick={() => setStep("select")} className="w-full py-2 text-xs text-gray-400 hover:text-white">← Go back</button>
          </div>
        )}

        {step === "details" && (
          <div className="space-y-4">
            <OPayWarning />
            <div className="p-5 rounded-2xl bg-white/5 border border-white/10 text-sm divide-y divide-white/10">
              <div className="flex justify-between py-2">
                <span className="text-gray-400">{theme.bankRowLabel}</span>
                <span className="font-medium">{theme.bankRowValue}</span>
              </div>
              <div className="flex justify-between py-2 items-center">
                <span className="text-gray-400">{theme.accountRowLabel}</span>
                <div className="flex items-center gap-2">
                  <span className="font-medium">{theme.accountNumber}</span>
                  <button onClick={copyAccount} className="text-xs bg-white/10 px-3 py-1 rounded-lg hover:bg-white/20">Copy</button>
                </div>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-gray-400">Name</span>
                <span className="font-medium">{theme.accountName}</span>
              </div>
              <div className="flex justify-between py-3 font-semibold">
                <span>Amount</span>
                <span className={theme.amountText}>{theme.currencySymbol}{theme.amount.toLocaleString()}</span>
              </div>
            </div>
            <button
              onClick={() => setStep("submitted")}
              className={`w-full py-3 rounded-xl ${theme.completeBtn} shadow-lg`}
            >
              I Have Completed My Payment
            </button>
          </div>
        )}

        {step === "submitted" && (
          <div className="text-center space-y-4">
            <div className="text-5xl">✅</div>
            <h3 className="text-xl font-bold">Payment Submitted!</h3>
            <p className="text-gray-400 text-sm">
              Your account will be activated once your payment is confirmed.
            </p>
            <div className="p-4 rounded-xl bg-yellow-500/10 border border-yellow-400/30 text-yellow-300 text-sm">
              ⏳ Activation usually takes a few hours. You will be notified.
            </div>

            <SupportButtons proof={true} />

            <div className="pt-2 space-y-2">
              <p className="text-xs text-gray-400">Having a payment problem?</p>
              <a
                href={buildMessages().waHelp}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 transition text-white text-sm"
              >
                💬 Report Payment Problem on WhatsApp
              </a>
              <a
                href={buildMessages().emailHelp}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 transition text-white text-sm"
              >
                📧 Report Payment Problem by Email
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentPage;
